import { useToast } from "@/components/ui/use-toast";
import { MEDIA_CONSTRAINTS, VIDEO_CALL_END_REASONS, type VideoCallEndReason } from "@/constants/video-call";
import { MediaState } from "@/types/video-call";
import { AudioPlayer } from "@/utils/audio";
import { TimelineEventService } from "@/services/video-call/timelineEventService";
import {
  validateCloudConnectionStability,
  publishTrackSafely,
  isCloudNegotiationError,
  handleCloudError,
  createConnectionDebouncer
} from "@/utils/livekitCloudUtils";
import {
  ParticipantEvent,
  RemoteParticipant,
  RemoteTrack,
  Room,
  Track,
  LocalTrackPublication,
} from "livekit-client";
import { useCallback, useEffect, useRef, useState } from "react";
import * as Sentry from "@sentry/react";
import { logCategorizedError, logRetryAttempt, logPerformanceIssue } from "@/utils/errorCategorization";

const NEGOTIATION_TIMEOUT_DEFAULT_MS = 12000;

interface UseMediaControlsProps {
  room: Room | null;
  connected: boolean;
  callActive: boolean;
  screenSharingEnabled: boolean;
  echoCancellation?: boolean;
  validatedScreenStream?: MediaStream;
  callId?: string;
  organizationId?: string;
  selectedDevices?: {
    videoDeviceId?: string;
    audioDeviceId?: string;
    audioOutputDeviceId?: string;
  };
  onAudioLevelChange?: (level: number) => void;
  onAgentSpeaking?: (speaking: boolean) => void;
  onEndCall?: (reason: VideoCallEndReason) => void;
}

export const useMediaControls = ({
  room,
  connected,
  callActive,
  screenSharingEnabled,
  echoCancellation = true,
  validatedScreenStream,
  callId,
  organizationId,
  selectedDevices,
  onAudioLevelChange,
  onAgentSpeaking,
  onEndCall,
}: UseMediaControlsProps) => {
  const { toast } = useToast();

  // Log when selectedDevices are received
  useEffect(() => {
    Sentry.logger.debug('Received selectedDevices in useMediaControls', {
      component: 'useMediaControls',
      selectedDevices,
      deviceKeys: Object.keys(selectedDevices || {}),
      audioOutputDeviceId: selectedDevices?.audioOutputDeviceId
    });
  }, [selectedDevices]);

  // Log when call becomes active (this is when violations can be detected)
  useEffect(() => {
    if (callActive) {
      Sentry.logger.info("Call now active - violation detection enabled", {
        component: 'useMediaControls',
        timestamp: new Date().toISOString(),
        callActive: true,
        connected,
        roomState: room?.state,
        violationDetectionEnabled: true,
        monitoringScope: "All proctoring violations can now be detected",
        status: "CALL_ACTIVE"
      });
    } else {
      Sentry.logger.debug("Call not active - violation detection disabled", {
        component: 'useMediaControls',
        timestamp: new Date().toISOString(),
        callActive: false,
        connected,
        roomState: room?.state,
        violationDetectionEnabled: false
      });
    }
  }, [callActive, connected, room?.state]);

  // Log when connection state changes
  useEffect(() => {
    if (connected) {
      console.log("🔗 [PROCTORED] ROOM CONNECTION ESTABLISHED:", {
        timestamp: new Date().toISOString(),
        connected: true,
        callActive,
        roomState: room?.state,
        readyForViolationDetection: callActive && connected,
        status: "CONNECTED"
      });
    } else {
      console.log("🔌 [PROCTORED] Room disconnected:", {
        timestamp: new Date().toISOString(),
        connected: false,
        callActive,
        roomState: room?.state,
        violationDetectionActive: false
      });
    }
  }, [connected, callActive, room?.state]);

  // Store participant event handlers to prevent memory leaks
  const participantHandlersRef = useRef<
    WeakMap<RemoteParticipant, (track: RemoteTrack) => void>
  >(new WeakMap());

  // Store audio context and monitoring timeouts for cleanup
  const audioContextsRef = useRef<Map<string, AudioContext>>(new Map());
  const animationFramesRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map()); // Now stores timeout IDs instead of animation frames
  const screenShareTracksRef = useRef<MediaStreamTrack[]>([]);
  const trackEndHandlersRef = useRef<Map<MediaStreamTrack, () => void>>(
    new Map()
  );

  // Track which remote tracks already have 'ended' listeners to prevent memory leak
  // (MaxListenersExceededWarning: 101 ended listeners)
  const remoteTracksWithEndedListenersRef = useRef<Set<string>>(new Set());

  // Track if call is ending properly (agent finished or user clicked end)
  const isCallEndingProperlyRef = useRef<boolean>(false);
  
  // Track the last time we received any ending signal for safety
  const lastEndingSignalTimeRef = useRef<number>(0);
  
  // Track if we're in the process of starting screen share (to avoid false warnings)
  const isStartingScreenShareRef = useRef<boolean>(false);
  
  // Track if we're currently processing a screen share end event (to prevent duplicates)
  const isProcessingScreenShareEndRef = useRef<boolean>(false);
  
  // Enhanced detection for Chrome's "Stop sharing" button
  const trackStopMethod = useRef<"app_button" | "browser_button" | "unknown">("unknown");
  const trackStopTimestamp = useRef<number>(0);
  
  // Track the source of the current screen share stream for recovery logic
  const currentStreamSourceRef = useRef<"pre-validated" | "new-request" | "unknown">("unknown");
  // Track retry attempts for screen-share publishing to avoid infinite reconnect loops
  const screenShareRetryCountRef = useRef<number>(0);
  
  // Create debouncer for screen share connection attempts
  const screenShareDebouncer = useRef(createConnectionDebouncer(3000)); // 3 second minimum interval

  // Serialize LiveKit negotiations to prevent overlapping SDP offers
  const negotiationQueueTailRef = useRef<Promise<void>>(Promise.resolve());
  const activeNegotiationLabelRef = useRef<string | null>(null);

  const runWithNegotiationGate = useCallback(
    async <T>(
      label: string,
      operation: () => Promise<T>,
      options?: { timeoutMs?: number }
    ): Promise<T> => {
      const timeoutMs = options?.timeoutMs ?? NEGOTIATION_TIMEOUT_DEFAULT_MS;
      const previous = negotiationQueueTailRef.current;

      let release: () => void = () => {};
      const current = new Promise<void>((resolve) => {
        release = resolve;
      });

      negotiationQueueTailRef.current = previous.then(() => current, () => current);

      if (activeNegotiationLabelRef.current) {
        console.log(
          `[useMediaControls] Waiting for negotiation "${activeNegotiationLabelRef.current}" before starting "${label}"`
        );
      }

      await previous.catch((error) => {
        if (error) {
          console.warn("[useMediaControls] Previous negotiation rejected", {
            label: activeNegotiationLabelRef.current,
            error,
          });
        }
      });

      activeNegotiationLabelRef.current = label;
      const startTime = Date.now();

      let timeoutId: ReturnType<typeof setTimeout> | undefined;
      const timeoutPromise = new Promise<never>((_, reject) => {
        timeoutId = setTimeout(() => {
          const timeoutError = new Error(
            `Negotiation "${label}" timed out after ${timeoutMs}ms`
          );
          (timeoutError as any).name = "NegotiationTimeoutError";
          reject(timeoutError);
        }, timeoutMs);
      });

      try {
        const result = await Promise.race([
          Promise.resolve().then(operation),
          timeoutPromise,
        ]);
        return result;
      } catch (error) {
        if ((error as Error)?.name === "NegotiationTimeoutError") {
          logCategorizedError(error, {
            component: "useMediaControls",
            operation: `${label}_timeout`,
            callId,
            organizationId,
            metadata: {
              timeoutMs,
              activeNegotiation: activeNegotiationLabelRef.current,
            },
          });
        }
        throw error;
      } finally {
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
        const duration = Date.now() - startTime;
        console.log(
          `[useMediaControls] Negotiation "${label}" completed in ${duration}ms`
        );
        activeNegotiationLabelRef.current = null;
        release();
      }
    },
    [callId, organizationId]
  );

  // Media state
  const [mediaState, setMediaState] = useState<MediaState>({
    isMuted: false,
    isVideoEnabled: true,
    isSpeakerMuted: false,
    screenSharingActive: false,
    screenShare: null,
    screenShareInterrupted: false,
    showScreenShareEndedWarning: false,
    screenShareEndReason: undefined,
    showCameraDisconnectedWarning: false,
    cameraDisconnectReason: undefined,
    cameraInterrupted: false,
  });

  // Refs for video elements
  const localVideoRef = useRef<HTMLVideoElement>(null);
  const remoteVideoRef = useRef<HTMLVideoElement>(null);
  const remoteAudioRef = useRef<HTMLAudioElement>(null);
  const screenShareRef = useRef<HTMLVideoElement>(null);
  const secondaryCameraRef = useRef<HTMLVideoElement>(null);

  // Helper function to safely clean up video element streams
  const cleanupVideoElement = useCallback(
    (videoElement: HTMLVideoElement | null) => {
      if (videoElement && videoElement.srcObject) {
        const stream = videoElement.srcObject as MediaStream;
        stream.getTracks().forEach((track) => {
          track.stop();
        });
        videoElement.srcObject = null;
      }
    },
    []
  );

  const detectAndValidateScreenShareScope = useCallback(
    async (
      stream: MediaStream
    ): Promise<{
      isValid: boolean;
      detectedScope: string;
      confidence: number;
    }> => {
      const videoTrack = stream.getVideoTracks()[0];
      if (!videoTrack) {
        return { isValid: false, detectedScope: "unknown", confidence: 0 };
      }

      const settings = videoTrack.getSettings();

      // Method 1: Direct API detection using displaySurface
      if (settings.displaySurface) {
        const isValid = settings.displaySurface === "monitor";
        return {
          isValid,
          detectedScope: settings.displaySurface,
          confidence: 1.0,
        };
      }

      // Method 2: Resolution analysis fallback
      const { width = 0, height = 0 } = settings;
      const aspectRatio = width / height;
      const pixels = width * height;

      // Common monitor resolutions that suggest full screen sharing
      const monitorSignatures = [
        { w: 1920, h: 1080 },
        { w: 2560, h: 1440 },
        { w: 3840, h: 2160 },
        { w: 1366, h: 768 },
        { w: 1280, h: 720 },
        { w: 1440, h: 900 },
        { w: 2048, h: 1152 },
        { w: 3440, h: 1440 },
        { w: 5120, h: 2880 },
      ];

      const tolerance = 50;
      const isMonitorResolution = monitorSignatures.some(
        (sig) =>
          Math.abs(width - sig.w) <= tolerance &&
          Math.abs(height - sig.h) <= tolerance
      );

      const isHighResolution = pixels > 1000000;
      const isStandardAspectRatio =
        Math.abs(aspectRatio - 16 / 9) < 0.1 ||
        Math.abs(aspectRatio - 16 / 10) < 0.1 ||
        Math.abs(aspectRatio - 4 / 3) < 0.1;

      if (isMonitorResolution && isHighResolution) {
        return { isValid: true, detectedScope: "monitor", confidence: 0.9 };
      } else if (isHighResolution && isStandardAspectRatio) {
        return { isValid: true, detectedScope: "monitor", confidence: 0.7 };
      } else if (pixels < 500000 || aspectRatio > 2.5 || aspectRatio < 0.5) {
        return {
          isValid: false,
          detectedScope: "window_or_tab",
          confidence: 0.8,
        };
      }

      return { isValid: true, detectedScope: "uncertain", confidence: 0.3 };
    },
    []
  );

  // Forward declaration for applyAudioOutputDevice
  const applyAudioOutputDeviceRef = useRef<(() => Promise<void>) | null>(null);

  const handleRemoteTrack = useCallback(
    (track: RemoteTrack, participant: RemoteParticipant) => {
      if (track.kind === Track.Kind.Video) {
        // Check if this is a screen share track
        if (track.source === Track.Source.ScreenShare) {
          // Attach screen share to screenShareRef (hidden but still recording)
          if (screenShareRef.current) {
            track.attach(screenShareRef.current);
            console.log("Remote screen share track attached for recording");
          }
        } else {
          // Regular camera video - attach to primary video ref
          if (remoteVideoRef.current) {
            cleanupVideoElement(remoteVideoRef.current);
            track.attach(remoteVideoRef.current);
          }
          // Also attach to secondary camera ref if screen sharing is active
          if (mediaState.screenSharingActive && secondaryCameraRef.current) {
            cleanupVideoElement(secondaryCameraRef.current);
            track.attach(secondaryCameraRef.current);
          }
        }
      } else if (track.kind === Track.Kind.Audio && remoteAudioRef.current) {
        track.attach(remoteAudioRef.current);
        
        // Apply selected audio output device using the centralized function
        // Use setTimeout to allow the audio element to initialize
        setTimeout(() => {
          applyAudioOutputDeviceRef.current?.();
        }, 100);
        
        // Also apply when audio starts playing
        const handleAudioPlay = () => {
          applyAudioOutputDeviceRef.current?.();
        };
        
        remoteAudioRef.current.addEventListener('play', handleAudioPlay, { once: true });
        remoteAudioRef.current.addEventListener('canplay', handleAudioPlay, { once: true });

        // Set up audio level monitoring for agent speaking detection
        if (track.mediaStreamTrack) {
          const trackId = track.sid || track.mediaStreamTrack.id;

          // Clean up any existing audio context for this track
          const existingContext = audioContextsRef.current.get(trackId);
          if (existingContext) {
            existingContext.close();
            audioContextsRef.current.delete(trackId);
          }

          const existingTimeoutId = animationFramesRef.current.get(trackId);
          if (existingTimeoutId) {
            clearTimeout(existingTimeoutId);
            animationFramesRef.current.delete(trackId);
          }

          const audioContext = new AudioContext();
          audioContextsRef.current.set(trackId, audioContext);

          const source = audioContext.createMediaStreamSource(
            new MediaStream([track.mediaStreamTrack])
          );
          const analyser = audioContext.createAnalyser();
          analyser.fftSize = 256;
          source.connect(analyser);

          const dataArray = new Uint8Array(analyser.frequencyBinCount);
          let lastSpeechTime = 0;
          let isActive = true;

          const checkAudioLevel = () => {
            if (!isActive) return;

            analyser.getByteFrequencyData(dataArray);
            const average =
              dataArray.reduce((a, b) => a + b) / dataArray.length;

            if (average > 10) {
              lastSpeechTime = Date.now();
              onAgentSpeaking?.(true);
              onAudioLevelChange?.(Math.min(average / 50, 4));
            } else {
              if (Date.now() - lastSpeechTime > 500) {
                onAgentSpeaking?.(false);
              }
            }

            if (connected && callActive && isActive) {
              // Changed from requestAnimationFrame (60fps) to 30-second intervals for LiveKit Cloud stability
              const timeoutId = setTimeout(checkAudioLevel, 30000); // 30 seconds
              animationFramesRef.current.set(trackId, timeoutId);
            }
          };

          checkAudioLevel();

          // Clean up when track ends - but only add listener once per track
          // This prevents MaxListenersExceededWarning (101 ended listeners)
          if (!remoteTracksWithEndedListenersRef.current.has(trackId)) {
            remoteTracksWithEndedListenersRef.current.add(trackId);
            track.on("ended", () => {
              isActive = false;
              const context = audioContextsRef.current.get(trackId);
              if (context) {
                context.close();
                audioContextsRef.current.delete(trackId);
              }
              const timeoutId = animationFramesRef.current.get(trackId);
              if (timeoutId) {
                clearTimeout(timeoutId);
                animationFramesRef.current.delete(trackId);
              }
              // Remove from tracking set when track ends
              remoteTracksWithEndedListenersRef.current.delete(trackId);
            });
          }
        }
      }
    },
    [
      connected,
      callActive,
      mediaState.screenSharingActive,
      screenShareRef,
      remoteVideoRef,
      secondaryCameraRef,
      remoteAudioRef,
      selectedDevices, // Add selectedDevices here
      onAudioLevelChange,
      onAgentSpeaking,
    ]
  );

  const handleRemoteParticipant = useCallback(
    (participant: RemoteParticipant) => {
      // Process existing tracks
      participant.trackPublications.forEach((publication) => {
        if (publication.track) {
          handleRemoteTrack(publication.track, participant);
        }
      });

      // Check if we already have a handler for this participant
      const existingHandler = participantHandlersRef.current.get(participant);
      if (existingHandler) {
        // Remove the existing handler first
        participant.off(ParticipantEvent.TrackSubscribed, existingHandler);
      }

      // Create a new handler
      const trackSubscribedHandler = (track: RemoteTrack) => {
        handleRemoteTrack(track, participant);
      };

      // Store the handler reference
      participantHandlersRef.current.set(participant, trackSubscribedHandler);

      // Add the listener
      participant.on(ParticipantEvent.TrackSubscribed, trackSubscribedHandler);

      // Return cleanup function
      return () => {
        const handler = participantHandlersRef.current.get(participant);
        if (handler) {
          participant.off(ParticipantEvent.TrackSubscribed, handler);
          participantHandlersRef.current.delete(participant);
        }
      };
    },
    [handleRemoteTrack]
  );

  const performEnableLocalMedia = useCallback(
    async (targetRoom: Room) => {
      console.log('🎯 [enableLocalMedia] Called with selectedDevices:', selectedDevices);
      
      // LiveKit Cloud connection state validation using utility
      if (!targetRoom || targetRoom.state !== 'connected') {
        console.log("Room not ready for media publishing - waiting for stable LiveKit Cloud connection");
        try {
          await validateCloudConnectionStability(targetRoom, { stabilityTimeout: 5000 });
        } catch (error) {
          // Log LiveKit Cloud readiness failure to Sentry
          logCategorizedError(error, {
            component: 'useMediaControls',
            operation: 'validateCloudConnectionStability',
            callId,
            organizationId,
            metadata: {
              roomState: targetRoom?.state,
              stabilityTimeout: 5000
            }
          });
          throw error;
        }
      }
      
      try {
        // Enable camera with high quality constraints
        if (mediaState.isVideoEnabled) {
          // Check if camera is already working - don't interfere if it is
          if (localVideoRef.current && localVideoRef.current.srcObject) {
            const currentStream = localVideoRef.current.srcObject as MediaStream;
            if (currentStream && currentStream.active && currentStream.getVideoTracks().length > 0) {
              const videoTrack = currentStream.getVideoTracks()[0];
              const settings = videoTrack.getSettings();
              if (videoTrack.readyState === 'live' && settings.width && settings.width > 2) {
                console.log("Camera already working, skipping re-initialization:", {
                  dimensions: `${settings.width}x${settings.height}`,
                  streamId: currentStream.id,
                  trackState: videoTrack.readyState
                });
                
                // Still proceed with microphone setup if needed
                if (!mediaState.isMuted) {
                  // Check if microphone is already enabled
                  const audioTracks = Array.from(targetRoom.localParticipant.audioTrackPublications.values());
                  if (audioTracks.length === 0 || !audioTracks.some(t => t.track && !t.isMuted)) {
                    console.log("Setting up microphone while preserving camera...");
                    await targetRoom.localParticipant.setMicrophoneEnabled(true, {
                      deviceId: selectedDevices?.audioDeviceId, // Use selected microphone
                    });
                  }
                }
                return; // Exit early - camera is already working
              }
            }
          }

          console.log("Enabling camera for local participant...");
          console.log("Attempting to enable camera through LiveKit...");

          try {
            // First disable camera to ensure clean state
            await targetRoom.localParticipant.setCameraEnabled(false);
            await new Promise((resolve) => setTimeout(resolve, 200));

            // Now enable with LiveKit Cloud optimized constraints and selected device
            await targetRoom.localParticipant.setCameraEnabled(true, {
              resolution: {
                width: 960, // Reduced for Cloud stability
                height: 540, // Reduced for Cloud stability
                frameRate: 25, // Slightly reduced for Cloud
              },
              deviceId: selectedDevices?.videoDeviceId, // Use selected camera
            });
            console.log("LiveKit camera enabled successfully");
          } catch (cameraError) {
            console.error(
              "Failed to enable camera with constraints:",
              cameraError
            );
            // Try without constraints
            try {
              await targetRoom.localParticipant.setCameraEnabled(true);
              console.log("Camera enabled without constraints");
            } catch (fallbackError) {
              console.error(
                "Failed to enable camera without constraints:",
                fallbackError
              );

              // Last resort - direct getUserMedia
              if (localVideoRef.current) {
                try {
                  console.log(
                    "Attempting direct getUserMedia as last resort..."
                  );
                  const directStream =
                    await navigator.mediaDevices.getUserMedia({
                      video: {
                        width: { ideal: 960 }, // Reduced for Cloud
                        height: { ideal: 540 }, // Reduced for Cloud
                        frameRate: { ideal: 25 }, // Reduced for Cloud
                      },
                    });
                  localVideoRef.current.srcObject = directStream;
                  console.log("Direct getUserMedia successful");
                } catch (directError) {
                  console.error(
                    "Direct getUserMedia also failed:",
                    directError
                  );
                }
              }
            }
          }

          // Wait for LiveKit to initialize a proper camera track
          let cameraTrack = null;
          let attempts = 0;
          const maxAttempts = 10;
          
          while (!cameraTrack && attempts < maxAttempts) {
            await new Promise((resolve) => setTimeout(resolve, 300));
            attempts++;
            
            const videoPublications = Array.from(
              targetRoom.localParticipant.videoTrackPublications.values()
            );
            console.log(`Camera track search attempt ${attempts}: ${videoPublications.length} publications`);

            for (const publication of videoPublications) {
              if (
                publication.track &&
                publication.source === Track.Source.Camera
              ) {
                const trackSettings = publication.track.mediaStreamTrack?.getSettings();
                const hasValidDimensions = trackSettings?.width && trackSettings.width > 2 && trackSettings?.height && trackSettings.height > 2;
                
                if (hasValidDimensions) {
                  cameraTrack = publication.track;
                  console.log("Found valid camera track:", {
                    trackId: cameraTrack.sid,
                    dimensions: `${trackSettings.width}x${trackSettings.height}`,
                    attempt: attempts
                  });
                  break;
                } else {
                  console.log(`Waiting for valid camera track (attempt ${attempts}), current dimensions: ${trackSettings?.width}x${trackSettings?.height}`);
                }
              }
            }
          }

          if (cameraTrack && localVideoRef.current) {
            // Clean up any existing stream
            cleanupVideoElement(localVideoRef.current);

            // Ensure the track is not muted
            if (cameraTrack.isMuted) {
              console.log("Camera track is muted, unmuting...");
              await cameraTrack.unmute();
            }

            // Ensure the track is enabled
            if (!cameraTrack.mediaStreamTrack?.enabled) {
              console.log("Camera track is disabled, enabling...");
              cameraTrack.enable();
            }

            // Use the attach method from LiveKit
            cameraTrack.attach(localVideoRef.current);
            
            const trackSettings = cameraTrack.mediaStreamTrack?.getSettings();
            console.log(
              "Camera track attached to local video using LiveKit attach method",
              {
                trackId: cameraTrack.sid,
                isMuted: cameraTrack.isMuted,
                isEnabled: cameraTrack.mediaStreamTrack?.enabled,
                dimensions: `${trackSettings?.width}x${trackSettings?.height}`,
                mediaStreamTrack: {
                  enabled: cameraTrack.mediaStreamTrack?.enabled,
                  muted: cameraTrack.mediaStreamTrack?.muted,
                  readyState: cameraTrack.mediaStreamTrack?.readyState,
                },
              }
            );

            // Wait a bit for the track to fully initialize
            await new Promise((resolve) => setTimeout(resolve, 500));

            // Ensure video element is playing with null safety check
            if (localVideoRef.current && localVideoRef.current.paused) {
              try {
                await localVideoRef.current.play();
                console.log("Video element started playing");
              } catch (playError) {
                console.error("Failed to play video element:", playError);
              }
            }

            // Verify the video element has a stream with null safety check
            if (localVideoRef.current && localVideoRef.current.srcObject) {
              const stream = localVideoRef.current.srcObject as MediaStream;
              const videoTracks = stream.getVideoTracks();
              console.log("Video element has srcObject:", {
                id: stream.id,
                active: stream.active,
                videoTracks: videoTracks.length,
                audioTracks: stream.getAudioTracks().length,
                firstVideoTrack: videoTracks[0]
                  ? {
                      enabled: videoTracks[0].enabled,
                      muted: videoTracks[0].muted,
                      readyState: videoTracks[0].readyState,
                      label: videoTracks[0].label,
                      settings: videoTracks[0].getSettings(),
                    }
                  : null,
              });

              // Just log the current state, don't try to fix it here since we already handled it above
              const videoTrack = videoTracks[0];
              if (!stream.active || !videoTrack) {
                console.warn("Stream or video track issue detected", {
                  active: stream.active,
                  hasVideoTrack: !!videoTrack,
                });
              }
            } else {
              console.warn("Video element srcObject is null after attach");
            }
          } else {
            console.warn(
              "Camera track not found or localVideoRef not available",
              {
                hasCameraTrack: !!cameraTrack,
                hasLocalVideoRef: !!localVideoRef.current,
              }
            );

            // Try direct getUserMedia as last resort
            if (localVideoRef.current && !cameraTrack) {
              try {
                console.log(
                  "Attempting direct getUserMedia as camera track not found"
                );
                const directStream = await navigator.mediaDevices.getUserMedia({
                  video: {
                    width: { ideal: 1920 },
                    height: { ideal: 1080 },
                    frameRate: { ideal: 30 },
                  },
                });
                localVideoRef.current.srcObject = directStream;
                console.log("Direct getUserMedia stream attached successfully");
              } catch (directError) {
                console.error("Direct getUserMedia failed:", directError);
              }
            }
          }
        }

        // Enable microphone with high quality audio
        if (!mediaState.isMuted) {
          console.log("Attempting to enable microphone...");

          try {
            await targetRoom.localParticipant.setMicrophoneEnabled(true, {
              deviceId: selectedDevices?.audioDeviceId, // Use selected microphone
            });
            await new Promise((resolve) => setTimeout(resolve, 1000));

            const audioTrack =
              targetRoom.localParticipant.audioTrackPublications.values().next()
                .value?.track;
            if (audioTrack) {
              console.log("Audio track enabled and published:", audioTrack);
            } else {
              console.warn("Audio track not found, trying manual approach...");

              const { createLocalAudioTrack } = await import("livekit-client");
              const localAudioTrack = await createLocalAudioTrack(
                MEDIA_CONSTRAINTS.audio
              );

              await targetRoom.localParticipant.publishTrack(localAudioTrack, {
                name: "microphone",
                source: Track.Source.Microphone,
              });

              console.log(
                "Manual audio track created and published:",
                localAudioTrack
              );
            }
          } catch (micError) {
            console.error("Error enabling microphone:", micError);

            // Log microphone failure to Sentry
            logCategorizedError(micError, {
              component: 'useMediaControls',
              operation: 'enableMicrophone',
              callId,
              organizationId,
              metadata: {
                selectedAudioDeviceId: selectedDevices?.audioDeviceId,
                roomState: targetRoom?.state,
                isMuted: mediaState.isMuted
              }
            });

            throw micError;
          }
        } else {
          console.log("Microphone not enabled because isMuted is true");
        }
      } catch (error) {
        console.error("Error enabling local media:", error);

        // Log general media enabling failure to Sentry
        logCategorizedError(error, {
          component: 'useMediaControls',
          operation: 'enableLocalMedia',
          callId,
          organizationId,
          metadata: {
            isVideoEnabled: mediaState.isVideoEnabled,
            isMuted: mediaState.isMuted,
            selectedDevices,
            roomState: targetRoom?.state
          }
        });
      }
    },
    [mediaState.isVideoEnabled, mediaState.isMuted, cleanupVideoElement, selectedDevices]
  );

  const enableLocalMedia = useCallback(
    async (targetRoom: Room) => {
      if (!targetRoom) {
        console.warn("Cannot enable local media: room not available");
        return;
      }

      if (enableLocalMediaInProgressRef.current && enableLocalMediaPromiseRef.current) {
        console.log("enableLocalMedia already in progress, awaiting existing negotiation");
        return enableLocalMediaPromiseRef.current;
      }

      enableLocalMediaInProgressRef.current = true;

      const negotiationPromise = runWithNegotiationGate(
        "enableLocalMedia",
        () => performEnableLocalMedia(targetRoom),
        { timeoutMs: NEGOTIATION_TIMEOUT_DEFAULT_MS }
      )
        .catch((error) => {
          if ((error as Error)?.name === "NegotiationTimeoutError") {
            console.error("enableLocalMedia negotiation timed out", error);
          }
          return;
        })
        .finally(() => {
          enableLocalMediaInProgressRef.current = false;
          enableLocalMediaPromiseRef.current = null;
        });

      enableLocalMediaPromiseRef.current = negotiationPromise;

      return negotiationPromise;
    },
    [performEnableLocalMedia, runWithNegotiationGate]
  );

  const toggleMicrophone = useCallback(async () => {
    if (!room) {
      console.warn("Cannot toggle microphone: room not connected");
      return;
    }

    try {
      console.log(
        `Toggling microphone from ${
          mediaState.isMuted ? "muted" : "unmuted"
        } to ${mediaState.isMuted ? "unmuted" : "muted"}`
      );
      
      // Track microphone operation to prevent camera interference
      recentMicrophoneOperationRef.current = Date.now();
      
      const deviceConstraints = selectedDevices?.audioDeviceId ? {
        deviceId: selectedDevices.audioDeviceId,
      } : undefined;
      
      await runWithNegotiationGate(
        "toggleMicrophone",
        () => room.localParticipant.setMicrophoneEnabled(mediaState.isMuted, deviceConstraints),
        { timeoutMs: NEGOTIATION_TIMEOUT_DEFAULT_MS }
      );
      setMediaState((prev) => ({ ...prev, isMuted: !prev.isMuted }));

      // Verify the change
      setTimeout(() => {
        const audioTracks = Array.from(
          room.localParticipant.audioTrackPublications.values()
        );
        console.log(
          "Audio tracks after toggle:",
          audioTracks.length,
          audioTracks.map((t) => ({
            trackSid: t.trackSid,
            isMuted: t.isMuted,
            isEnabled: t.track?.mediaStreamTrack?.enabled,
          }))
        );
      }, 500);
    } catch (error) {
      console.error("Error toggling microphone:", error);

      // Log microphone toggle failure to Sentry
      logCategorizedError(error, {
        component: 'useMediaControls',
        operation: 'toggleMicrophone',
        callId,
        organizationId,
        metadata: {
          currentlyMuted: mediaState.isMuted,
          selectedAudioDeviceId: selectedDevices?.audioDeviceId,
          roomState: room?.state
        }
      });
    }
  }, [room, mediaState.isMuted, runWithNegotiationGate, selectedDevices]);

  const toggleSpeaker = useCallback(() => {
    if (remoteAudioRef.current) {
      remoteAudioRef.current.muted = !mediaState.isSpeakerMuted;
      setMediaState((prev) => ({
        ...prev,
        isSpeakerMuted: !prev.isSpeakerMuted,
      }));
    }
  }, [mediaState.isSpeakerMuted]);

  // Generic pause function for all violations
  const pauseCallForViolation = useCallback((violationType: string, eventType: string = "call_paused") => {
    // Mute local microphone during violation
    if (room && room.state === "connected") {
      room.localParticipant.setMicrophoneEnabled(false);
    }

    // Mute remote audio during violation
    if (remoteAudioRef.current) {
      remoteAudioRef.current.muted = true;
    }

    // Send pause event with violation type
    if (room && room.state === "connected") {
      const encoder = new TextEncoder();
      const pauseData = encoder.encode(
        JSON.stringify({
          type: eventType,
          reason: violationType,
          timestamp: new Date().toISOString(),
        })
      );
      room.localParticipant.publishData(pauseData, { reliable: true });
    }
  }, [room]);

  // Generic resume function for all violations
  const resumeCallAfterViolation = useCallback((eventType: string = "call_resumed") => {
    // Re-enable microphone if it was enabled before
    if (room && room.state === "connected" && mediaState.isMuted === false) {
      room.localParticipant.setMicrophoneEnabled(true);
    }

    // Unmute remote audio if speaker was not muted
    if (remoteAudioRef.current && !mediaState.isSpeakerMuted) {
      remoteAudioRef.current.muted = false;
    }

    // Send resume event
    if (room && room.state === "connected") {
      const encoder = new TextEncoder();
      const resumeData = encoder.encode(
        JSON.stringify({
          type: eventType,
          timestamp: new Date().toISOString(),
        })
      );
      room.localParticipant.publishData(resumeData, { reliable: true });
    }
  }, [room, mediaState.isMuted, mediaState.isSpeakerMuted]);

  // Pause functions for different violation types
  const pauseCallForTabChangePenalty = useCallback(() => {
    pauseCallForViolation("tab_change", "tab_penalty_pause");
  }, [pauseCallForViolation]);

  const pauseCallForBlurViolation = useCallback(() => {
    pauseCallForViolation("face_blur", "call_paused");
  }, [pauseCallForViolation]);

  const pauseCallForCameraDisconnect = useCallback(() => {
    pauseCallForViolation("camera_disconnect", "call_paused");
  }, [pauseCallForViolation]);

  const pauseCallForFaceOutOfView = useCallback(() => {
    pauseCallForViolation("face_out_of_view", "call_paused");
  }, [pauseCallForViolation]);

  // Resume functions for different violation types
  const resumeCallAfterTabChangePenalty = useCallback(() => {
    resumeCallAfterViolation("tab_penalty_resumed");
  }, [resumeCallAfterViolation]);

  const resumeCallAfterBlurViolation = useCallback(() => {
    resumeCallAfterViolation("call_resumed");
  }, [resumeCallAfterViolation]);

  const resumeCallAfterCameraReconnect = useCallback(() => {
    resumeCallAfterViolation("call_resumed");
  }, [resumeCallAfterViolation]);

  const resumeCallAfterFaceInView = useCallback(() => {
    resumeCallAfterViolation("call_resumed");
  }, [resumeCallAfterViolation]);

  const handleCameraDisconnect = useCallback(
    (reason: "user_stopped" | "track_ended" | "permission_denied" | "device_disconnected") => {
      console.log("🔴 CAMERA: handleCameraDisconnect called with reason:", reason);
      console.log("🔴 CAMERA: isCallEndingProperlyRef.current:", isCallEndingProperlyRef.current);
      console.log("🔴 CAMERA: lastEndingSignalTimeRef.current:", lastEndingSignalTimeRef.current);
      
      // Check if call is ending properly (assistant ended or user clicked end)
      if (isCallEndingProperlyRef.current) {
        console.log("✅ CAMERA: Camera disconnect ignored - call is ending properly");
        return;
      }
      
      // Additional safety: Check if we received any ending signal recently (race condition protection)
      const now = Date.now();
      if (lastEndingSignalTimeRef.current && (now - lastEndingSignalTimeRef.current) < 3000) {
        console.log("✅ CAMERA: Camera disconnect ignored - recent ending signal detected");
        return;
      }
      
      // Only show warning if video was supposed to be enabled
      if (mediaState.isVideoEnabled) {
        // Play critical violation sound for camera disconnect
        AudioPlayer.playCriticalSound();
        
        setMediaState((prev) => ({
          ...prev,
          showCameraDisconnectedWarning: true,
          cameraDisconnectReason: reason,
          cameraInterrupted: true,
          // Don't turn off camera when assistant ends call
          isVideoEnabled: reason === "user_stopped" ? false : prev.isVideoEnabled,
        }));

        // Send camera disconnect event
        if (room && room.state === "connected") {
          const encoder = new TextEncoder();
          const data = encoder.encode(
            JSON.stringify({
              type: "camera_disconnected",
              reason,
              timestamp: new Date().toISOString(),
            })
          );
          room.localParticipant.publishData(data, { reliable: true });
        }

        // Pause call for camera disconnect
        pauseCallForCameraDisconnect();
        
        // End call after 3 seconds
        setTimeout(() => {
          if (onEndCall) {
            onEndCall(VIDEO_CALL_END_REASONS.CAMERA_VIOLATION);
          }
        }, 3000);
      }
    },
    [room, mediaState.isVideoEnabled, pauseCallForCameraDisconnect, onEndCall]
  );

  const toggleVideo = useCallback(async () => {
    if (!room) return;

    try {
      await runWithNegotiationGate(
        "toggleVideo",
        async () => {
          const newVideoState = !mediaState.isVideoEnabled;
          
          // If turning on camera, set state optimistically but verify it works
          if (newVideoState) {
            setMediaState((prev) => ({
              ...prev,
              isVideoEnabled: true,
            }));
            
            let retries = 0;
            try {
              await room.localParticipant.setCameraEnabled(true);
              
              // Wait for track to be published
              await new Promise((resolve) => setTimeout(resolve, 1000));
              
              // Verify camera actually started
              let cameraTrack = null;
              const maxRetries = 3;
              
              while (!cameraTrack && retries < maxRetries) {
                for (const publication of room.localParticipant.videoTrackPublications.values()) {
                  if (publication.track && publication.source === Track.Source.Camera) {
                    const trackSettings = publication.track.mediaStreamTrack?.getSettings();
                    // Check if it's a real track, not placeholder
                    if (trackSettings?.width && trackSettings.width > 2) {
                      cameraTrack = publication.track;
                      break;
                    }
                  }
                }
                
                if (!cameraTrack) {
                  console.log(`Camera track not ready, retry ${retries + 1}/${maxRetries}`);
                  await new Promise((resolve) => setTimeout(resolve, 1000));
                  retries++;
                }
              }
              
              if (cameraTrack && localVideoRef.current) {
                cleanupVideoElement(localVideoRef.current);
                cameraTrack.attach(localVideoRef.current);
                console.log("Camera track attached successfully");
                
                // Verify stream is active
                const stream = localVideoRef.current.srcObject as MediaStream;
                if (!stream || !stream.active || stream.getVideoTracks().length === 0) {
                  throw new Error("Stream verification failed");
                }
              } else {
                throw new Error("No valid camera track found after retries");
              }
              
            } catch (cameraError) {
              console.error("Failed to enable camera:", cameraError);

              // Log camera failure to Sentry
              logCategorizedError(cameraError, {
                component: 'useMediaControls',
                operation: 'enableCamera',
                callId,
                organizationId,
                metadata: {
                  selectedVideoDeviceId: selectedDevices?.videoDeviceId,
                  roomState: room?.state,
                  retryAttempts: retries
                }
              });

              // Revert state if camera failed to enable
              setMediaState((prev) => ({
                ...prev,
                isVideoEnabled: false,
              }));
              
              // Try direct getUserMedia as fallback
              try {
                console.log("Attempting direct getUserMedia fallback");
                const directStream = await navigator.mediaDevices.getUserMedia({
                  video: {
                    width: { ideal: 1280, min: 640 },
                    height: { ideal: 720, min: 480 },
                    frameRate: { ideal: 30, min: 15 },
                    facingMode: "user"
                  },
                });
                
                if (localVideoRef.current) {
                  cleanupVideoElement(localVideoRef.current);
                  localVideoRef.current.srcObject = directStream;
                  
                  // Update state to reflect camera is on
                  setMediaState((prev) => ({
                    ...prev,
                    isVideoEnabled: true,
                  }));
                  
                  console.log("Direct camera fallback successful");
                  
                  // Try to sync with LiveKit in background
                  setTimeout(async () => {
                    try {
                      await room.localParticipant.setCameraEnabled(false);
                      await room.localParticipant.setCameraEnabled(true);
                    } catch (err) {
                      console.error("Background LiveKit sync failed:", err);
                    }
                  }, 2000);
                }
              } catch (directError) {
                console.error("Direct getUserMedia fallback also failed:", directError);

                // Log final camera failure to Sentry
                logCategorizedError(directError, {
                  component: 'useMediaControls',
                  operation: 'fallbackGetUserMedia',
                  callId,
                  organizationId,
                  metadata: {
                    primaryError: cameraError instanceof Error ? cameraError.message : String(cameraError),
                    fallbackAttempted: true,
                    selectedVideoDeviceId: selectedDevices?.videoDeviceId
                  }
                });

                // Show error to user
                if (onEndCall) {
                  onEndCall(VIDEO_CALL_END_REASONS.CAMERA_FAILED);
                }
              }
            }
          } else {
            // Turning off camera
            await room.localParticipant.setCameraEnabled(false);
            setMediaState((prev) => ({
              ...prev,
              isVideoEnabled: false,
            }));
            handleCameraDisconnect("user_stopped");
          }
        },
        { timeoutMs: NEGOTIATION_TIMEOUT_DEFAULT_MS }
      );
      
    } catch (error) {
      console.error("Error toggling video:", error);

      // Log video toggle failure to Sentry
      logCategorizedError(error, {
        component: 'useMediaControls',
        operation: 'toggleVideo',
        callId,
        organizationId,
        metadata: {
          targetState: !mediaState.isVideoEnabled,
          currentState: mediaState.isVideoEnabled,
          selectedVideoDeviceId: selectedDevices?.videoDeviceId,
          roomState: room?.state
        }
      });

      // Ensure state reflects actual camera state
      setMediaState((prev) => ({
        ...prev,
        isVideoEnabled: false,
      }));
    }
  }, [room, mediaState.isVideoEnabled, onEndCall, runWithNegotiationGate, selectedDevices, handleCameraDisconnect, cleanupVideoElement]);

  // Helper to clean up screen share without warnings
  const cleanupScreenShare = useCallback(() => {
    setMediaState((prev) => {
      if (prev.screenShare) {
        prev.screenShare.getTracks().forEach((track) => track.stop());
      }
      return { 
        ...prev, 
        screenShare: null,
        screenSharingActive: false 
      };
    });
    
    // Clean up event listeners
    screenShareTracksRef.current.forEach((track) => {
      const handler = trackEndHandlersRef.current.get(track);
      if (handler) {
        track.removeEventListener("ended", handler);
        trackEndHandlersRef.current.delete(track);
      }
    });
    screenShareTracksRef.current = [];
    
    // Clean up video element
    cleanupVideoElement(screenShareRef.current);
  }, [cleanupVideoElement]);

  const handleScreenShareEnd = useCallback(
    async (reason: "user_stopped" | "track_ended" | "permission_denied") => {
      const endTime = new Date().toISOString();
      
      // Prevent duplicate processing
      if (isProcessingScreenShareEndRef.current) {
        console.log("🚨 [PROCTORED] Screen share end already being processed, skipping duplicate:", {
          reason,
          endTime,
          processingInProgress: true
        });
        return;
      }
      
      // Set processing flag
      isProcessingScreenShareEndRef.current = true;
      
      console.log("🚨 [PROCTORED] Screen share termination handler triggered:", {
        reason,
        endTime,
        isCallEndingProperly: isCallEndingProperlyRef.current,
        isStartingScreenShare: isStartingScreenShareRef.current,
        callActive,
        connected,
        processingStarted: true
      });
      
      // Clean up the stream immediately for security
      setMediaState((prev) => {
        if (prev.screenShare) {
          prev.screenShare.getTracks().forEach((track) => track.stop());
        }
        return { ...prev, screenShare: null };
      });

      // CRITICAL: Handle proctoring violation if call is active and not ending properly
      if (!isCallEndingProperlyRef.current && !isStartingScreenShareRef.current && callActive) {
        console.log("🚨 [PROCTORED] CRITICAL VIOLATION: Unexpected screen share termination during proctored session");
        
        // Determine violation severity based on reason
        const violationSeverity = reason === "user_stopped" ? "critical" : "critical";
        const violationMessage = reason === "user_stopped" 
          ? "Screen sharing manually stopped during proctored session"
          : reason === "permission_denied"
          ? "Screen sharing permission revoked during proctored session"
          : "Screen sharing connection lost during proctored session";

        // Update state with critical violation flags
        setMediaState((prev) => ({
          ...prev,
          screenSharingActive: false,
          showScreenShareEndedWarning: true,
          screenShareEndReason: reason,
          screenShareInterrupted: true,
          callPaused: true,
          pauseReason: "screen_share_lost",
        }));

        // Immediately pause all communication during violation
        if (room && room.state === "connected") {
          // Mute local microphone
          room.localParticipant.setMicrophoneEnabled(false);
          console.log("🚨 [PROCTORED] Local microphone muted due to screen share violation");
        }

        // Mute remote audio to pause session
        if (remoteAudioRef.current) {
          remoteAudioRef.current.muted = true;
          console.log("🚨 [PROCTORED] Remote audio muted due to screen share violation");
        }

        // Create timeline event for screen share violation
        if (callId) {
          const screenShareEvent = TimelineEventService.createScreenShareViolationEvent(
            'screen_share_stopped',
            reason,
            'critical',
            {
              trackStopMethod: trackStopMethod.current,
              callActive,
              connected,
              violationMessage,
              endTime
            }
          );
          
          TimelineEventService.addTimelineEvent(callId, screenShareEvent)
            .then(() => {
              console.log("🚨 [PROCTORED] Screen share violation timeline event created");
            })
            .catch(error => {
              console.error("🚨 [PROCTORED] Failed to create screen share violation timeline event:", error);
            });
        } else {
          console.warn("🚨 [PROCTORED] Cannot create timeline event - missing callId");
        }

        // Send critical violation event to backend with detailed context
        if (room && room.state === "connected") {
          const encoder = new TextEncoder();
          const violationData = encoder.encode(
            JSON.stringify({
              type: "proctoring_violation_critical",
              violation_type: "screen_share_terminated",
              reason,
              severity: violationSeverity,
              message: violationMessage,
              timestamp: endTime,
              session_context: {
                callActive,
                connected,
                trackStopMethod: trackStopMethod.current,
                userAgent: navigator.userAgent,
                timestamp: endTime
              }
            })
          );
          room.localParticipant.publishData(violationData, { reliable: true });
          console.log("🚨 [PROCTORED] Critical violation reported to backend");
        }
        
        // Critical violation - immediate session termination
        console.log("🚨 [PROCTORED] Initiating immediate session termination due to screen share violation");
        setTimeout(() => {
          if (onEndCall) {
            console.log("🚨 [PROCTORED] Executing call termination");
            onEndCall(VIDEO_CALL_END_REASONS.SCREENSHARE_VIOLATION);
          }
        }, 3000); // 3 second grace period to show violation warning

      } else if (isCallEndingProperlyRef.current) {
        // Call is ending properly by agent - graceful cleanup
        console.log("🎬 [PROCTORED] Graceful screen share cleanup - call ending properly");
        setMediaState((prev) => ({
          ...prev,
          screenSharingActive: false,
        }));
      } else {
        // Screen share ended outside active call context - safe cleanup
        setMediaState((prev) => ({
          ...prev,
          screenSharingActive: false,
        }));
      }

      // Security cleanup: Remove all track event listeners
      screenShareTracksRef.current.forEach((track) => {
        const handler = trackEndHandlersRef.current.get(track);
        if (handler) {
          track.removeEventListener("ended", handler);
          trackEndHandlersRef.current.delete(track);
        }
      });
      screenShareTracksRef.current = [];

      // Clear video element for security
      cleanupVideoElement(screenShareRef.current);

      // Send session termination event to backend for audit trail
      if (room && room.state === "connected") {
        const encoder = new TextEncoder();
        const terminationData = encoder.encode(
          JSON.stringify({
            type: "proctored_screen_share_terminated",
            reason,
            timestamp: endTime,
            was_violation: !isCallEndingProperlyRef.current && !isStartingScreenShareRef.current && callActive,
            session_context: {
              callActive,
              connected,
              isCallEndingProperly: isCallEndingProperlyRef.current,
              isStartingScreenShare: isStartingScreenShareRef.current
            }
          })
        );
        room.localParticipant.publishData(terminationData, { reliable: true });
      }

      
      // Reset processing flag after a delay to prevent race conditions
      setTimeout(() => {
        isProcessingScreenShareEndRef.current = false;
      }, 1000); // 1 second delay
    },
    [room, onEndCall, cleanupVideoElement, callActive, connected, toast]
  );

  const startScreenShare = useCallback(async () => {
    const functionStartTime = Date.now();
    
    // Apply connection debouncing to prevent rapid successive attempts
    if (!screenShareDebouncer.current.shouldAllow()) {
      return;
    }
    
    // Reset retry counter on each new attempt
    screenShareRetryCountRef.current = screenShareRetryCountRef.current || 0;
    console.log("🎬 [PROCTORED] Starting screen share session:", {
      timestamp: new Date().toISOString(),
      screenSharingActive: mediaState.screenSharingActive,
      hasValidatedStream: !!(validatedScreenStream && validatedScreenStream.active),
      roomConnected: room?.state === "connected",
      callActive,
      connected
    });

    // CRITICAL: Prevent screen share restart if there are active violations
    if (mediaState.showScreenShareEndedWarning || 
        mediaState.screenShareInterrupted ||
        (mediaState.callPaused && mediaState.pauseReason === "screen_share_lost")) {
      console.log("🚨 [PROCTORED] SCREEN SHARE START BLOCKED - Active violation detected:", {
        showScreenShareEndedWarning: mediaState.showScreenShareEndedWarning,
        screenShareInterrupted: mediaState.screenShareInterrupted,
        callPaused: mediaState.callPaused,
        pauseReason: mediaState.pauseReason,
        securityBlock: true
      });
      return;
    }

    if (!room || mediaState.screenSharingActive) {
      return;
    }
    
    // Mark that we're starting screen share to prevent false violation warnings
    isStartingScreenShareRef.current = true;

    // Clean up any existing screen share tracks first for security
    if (screenShareTracksRef.current.length > 0) {
      console.warn("🎬 [PROCTORED] Cleaning up existing screen share tracks for security");
      screenShareTracksRef.current.forEach((track) => {
        const handler = trackEndHandlersRef.current.get(track);
        if (handler) {
          track.removeEventListener("ended", handler);
          trackEndHandlersRef.current.delete(track);
        }
        track.stop();
      });
      screenShareTracksRef.current = [];
    }

    try {
      let stream: MediaStream;
      let validation: {
        isValid: boolean;
        detectedScope: string;
        confidence: number;
      };
      let streamSource: "pre-validated" | "new-request" = "pre-validated";

      // PRIORITY 1: Use pre-validated stream from setup if available and active
      if (validatedScreenStream && validatedScreenStream.active) {
        // Enhanced validation to ensure the pre-validated stream is still valid
        const videoTracks = validatedScreenStream.getVideoTracks();
        const hasValidVideoTrack = videoTracks.length > 0 && 
                                  videoTracks[0].readyState === 'live' &&
                                  videoTracks[0].enabled;

        if (hasValidVideoTrack) {
          // Additional check: Ensure the track hasn't been used elsewhere
          const videoTrack = videoTracks[0];
          const settings = videoTrack.getSettings();
          
          // Test if the track is actually producing valid frames
          const isProducingFrames = settings.width && settings.width > 0 && 
                                   settings.height && settings.height > 0;
          
          if (isProducingFrames) {
            stream = validatedScreenStream;
            validation = {
              isValid: true,
              detectedScope: "monitor", // Pre-validated streams are always full screen
              confidence: 1.0,
            };

            // NOTE: Do NOT clean up hidden video element here!
            // The hidden video keeps the stream alive. We must wait until AFTER
            // LiveKit has successfully published the track before removing it.
            // Cleanup will happen after publishTrackSafely() succeeds.
            console.log("🔒 [PROCTORED] Keeping hidden video element alive until LiveKit publishes track");

            console.log("🎬 [PROCTORED] Using pre-validated screen share stream from setup phase:", {
              streamId: validatedScreenStream.id,
              trackId: videoTrack.id,
              trackLabel: videoTrack.label,
              readyState: videoTrack.readyState,
              enabled: videoTrack.enabled,
              settings: settings,
              active: validatedScreenStream.active,
              trackCount: videoTracks.length
            });
            
            // CRITICAL: Add enhanced monitoring for pre-validated streams
            // Listen for any signs the stream might be ending
            videoTrack.addEventListener('ended', () => {
              console.log("🚨 [PROCTORED] Pre-validated stream track ended unexpectedly during use:", {
                timestamp: new Date().toISOString(),
                trackId: videoTrack.id,
                streamId: validatedScreenStream.id,
                wasActive: validatedScreenStream.active,
                readyState: videoTrack.readyState
              });
              
              // IMMEDIATE RECOVERY: Try to get a new stream if this was unexpected
              if (mediaState.screenSharingActive && callActive && !isCallEndingProperlyRef.current) {
                console.log("🔄 [PROCTORED] Attempting immediate screen share recovery due to pre-validated stream failure");
                setTimeout(() => {
                  // Clear the starting flag and try again
                  isStartingScreenShareRef.current = false;
                  startScreenShare();
                }, 500);
              }
            }, { once: true });
            
            // Monitor stream inactive events
            validatedScreenStream.addEventListener('inactive', () => {
              console.log("🚨 [PROCTORED] Pre-validated stream became inactive during use:", {
                timestamp: new Date().toISOString(),
                streamId: validatedScreenStream.id,
                trackStates: videoTracks.map(t => ({
                  id: t.id,
                  readyState: t.readyState,
                  enabled: t.enabled
                }))
              });
              
              // IMMEDIATE RECOVERY: Try to get a new stream if this was unexpected
              if (mediaState.screenSharingActive && callActive && !isCallEndingProperlyRef.current) {
                console.log("🔄 [PROCTORED] Attempting immediate screen share recovery due to stream becoming inactive");
                setTimeout(() => {
                  // Clear the starting flag and try again
                  isStartingScreenShareRef.current = false;
                  startScreenShare();
                }, 500);
              }
            }, { once: true });
            
          } else {
            console.log("🎬 [PROCTORED] Pre-validated stream track not producing valid frames:", {
              settings,
              readyState: videoTrack.readyState,
              enabled: videoTrack.enabled
            });
            
            // Clean up hidden video element if it exists
            if ((validatedScreenStream as any)._hiddenVideoCleanup) {
              (validatedScreenStream as any)._hiddenVideoCleanup();
            }
            
            validatedScreenStream.getTracks().forEach(track => track.stop());
            streamSource = "new-request";
          }
        } else {
          console.log("🎬 [PROCTORED] Pre-validated stream has invalid video track:", {
            trackCount: videoTracks.length,
            firstTrackState: videoTracks[0] ? {
              readyState: videoTracks[0].readyState,
              enabled: videoTracks[0].enabled,
              id: videoTracks[0].id
            } : null
          });
          
          // Clean up hidden video element if it exists
          if ((validatedScreenStream as any)._hiddenVideoCleanup) {
            (validatedScreenStream as any)._hiddenVideoCleanup();
          }
          
          validatedScreenStream.getTracks().forEach(track => track.stop());
          streamSource = "new-request";
        }
      } else {
        console.log("🎬 [PROCTORED] No valid pre-validated stream available:", {
          hasStream: !!validatedScreenStream,
          streamActive: validatedScreenStream?.active,
          trackCount: validatedScreenStream?.getTracks().length || 0
        });
        streamSource = "new-request";
      }

      // PRIORITY 2: Request new stream only if pre-validated stream is not available
      if (!stream) {
        console.log("🎬 [PROCTORED] Requesting new screen share stream with strict proctoring constraints");
        
        const displayMediaOptions = {
          video: {
            displaySurface: "monitor", // Enforce full screen sharing
            cursor: "always", // Always show cursor for transparency
            width: { ideal: 1920, max: 3840 }, // Support high resolution monitoring
            height: { ideal: 1080, max: 2160 },
            frameRate: { ideal: 30, max: 60 }, // Smooth monitoring
          } as MediaTrackConstraints,
          audio: {
            echoCancellation,
            noiseSuppression: true,
            autoGainControl: true,
          } as MediaTrackConstraints,
          preferCurrentTab: false, // Never allow tab sharing
          selfBrowserSurface: "exclude", // Exclude browser UI
        };

        try {
          console.log("🎬 [PROCTORED] Requesting display media - user dialog may appear now");
          const startTime = Date.now();
          
          stream = await navigator.mediaDevices.getDisplayMedia(displayMediaOptions);
          streamSource = "new-request";
          
          const endTime = Date.now();
          console.log("🎬 [PROCTORED] Display media request completed:", {
            duration: endTime - startTime,
            streamId: stream.id,
            trackCount: stream.getTracks().length
          });
        } catch (mediaError) {
          console.error("🎬 [PROCTORED] Failed to get display media:", mediaError);
          isStartingScreenShareRef.current = false;
          
          // Handle specific error cases for proctored sessions
          if (mediaError.name === "NotAllowedError") {
            toast({
              title: "Screen Sharing Permission Denied",
              description: "Screen sharing is required for proctored sessions. Please allow screen sharing and try again.",
              variant: "destructive",
            });
          } else if (mediaError.name === "NotFoundError") {
            toast({
              title: "No Screen Available",
              description: "No screen available for sharing. Please ensure you have a display connected.",
              variant: "destructive",
            });
          } else {
            toast({
              title: "Screen Sharing Failed",
              description: "Unable to start screen sharing. Please check your browser permissions and try again.",
              variant: "destructive",
            });
          }
          return;
        }

        // Validate the newly requested stream for proctoring compliance
        validation = await detectAndValidateScreenShareScope(stream);
        console.log("🎬 [PROCTORED] New stream validation result:", validation);

        if (!validation.isValid) {
          // Stop the invalid stream immediately
          stream.getTracks().forEach((track) => track.stop());
          isStartingScreenShareRef.current = false;

          let errorMessage = "Invalid screen sharing detected for proctored session. ";
          if (validation.detectedScope === "window_or_tab") {
            errorMessage += "You must share your entire screen, not just a window or tab. This is required for proctoring compliance.";
          } else {
            errorMessage += "For proctoring compliance, only entire screen sharing is permitted.";
          }

          toast({
            title: "Proctoring Violation: Invalid Screen Share",
            description: errorMessage,
            variant: "destructive",
          });

          // Provide specific guidance for proctored sessions
          setTimeout(() => {
            toast({
              title: "How to Share Your Screen Correctly",
              description: "When prompted, click 'Entire Screen' tab, select your main display, then click 'Share'. Do not select 'Window' or 'Chrome Tab'.",
            });
          }, 3000);

          return;
        }
      }

      // SECURITY: Validate stream integrity before proceeding
      const videoTracks = stream.getVideoTracks();
      if (videoTracks.length === 0) {
        console.error("🎬 [PROCTORED] No video tracks in screen share stream");
        isStartingScreenShareRef.current = false;
        toast({
          title: "Screen Share Error",
          description: "No video tracks found in screen share. Please try again.",
          variant: "destructive",
        });
        return;
      }

      let audioTracks: MediaStreamTrack[] = [];
      let tracks: MediaStreamTrack[] = [];

      await runWithNegotiationGate(
        "startScreenShare",
        async () => {
          // Update media state with proctoring-specific flags
          setMediaState((prev) => ({
            ...prev,
            screenShare: stream,
            screenSharingActive: true,
            screenShareInterrupted: false,
            // Clear any previous pause states
            callPaused: false,
            pauseReason: undefined,
          }));

          // Reset tracking flags for clean monitoring
          trackStopMethod.current = "unknown";
          currentStreamSourceRef.current = streamSource;
          
          // Clear the starting flag since we've successfully started
          isStartingScreenShareRef.current = false;

          // Start enhanced monitoring for proctored sessions
          monitorScreenShareEvents(stream);

          // Publish screen share to LiveKit Cloud with optimized settings
          console.log("🎬 [PROCTORED] Publishing screen share track to LiveKit Cloud");
          
          // Validate room is ready for publishing before attempting
          if (room.state !== 'connected') {
            throw new Error('LiveKit Cloud not ready for screen share publishing');
          }
          
          await publishTrackSafely(room, videoTracks[0], {
            name: "proctored_screen_share",
            source: Track.Source.ScreenShare,
            videoEncoding: {
              maxBitrate: 8_000_000, // Reduced for Cloud stability
              maxFramerate: 30, // Reduced for Cloud stability
            },
            // Cloud-specific optimizations
            dtx: false, // Keep false for screen share
            simulcast: false, // Disable simulcast for screen share on Cloud
          });

          // NOW it's safe to clean up the hidden video element - LiveKit is consuming the stream
          // This prevents the race condition where the browser terminates the stream
          // because there's no consumer during the gap between cleanup and publishing
          if ((stream as any)._hiddenVideoCleanup) {
            console.log("🧹 [PROCTORED] Cleaning up hidden video element - LiveKit now consuming stream");
            (stream as any)._hiddenVideoCleanup();
            delete (stream as any)._hiddenVideoCleanup;
          }

          // Publish screen share audio if available (for complete monitoring)
          audioTracks = stream.getAudioTracks();
          if (audioTracks.length > 0) {
            await room.localParticipant.publishTrack(audioTracks[0], {
              name: "proctored_screen_audio",
              source: Track.Source.ScreenShareAudio,
            });
          }

          // Clean up any previous screen share track references
          screenShareTracksRef.current.forEach((track) => {
            const handler = trackEndHandlersRef.current.get(track);
            if (handler) {
              track.removeEventListener("ended", handler);
              trackEndHandlersRef.current.delete(track);
            }
          });
          screenShareTracksRef.current = [];

          // Set up enhanced monitoring for track endings in proctored sessions
          tracks = stream.getTracks();
          screenShareTracksRef.current = tracks;

          tracks.forEach((track, index) => {

        const endHandler = () => {
          const endTime = new Date().toISOString();
          console.log("🔍 [PROCTORED] VIOLATION ANALYSIS - Track end detection:", {
            trackId: track.id,
            trackKind: track.kind,
            endTime,
            isCallEndingProperly: isCallEndingProperlyRef.current,
            isStartingScreenShare: isStartingScreenShareRef.current,
            callActive: callActive,
            connected: connected,
            trackReadyState: track.readyState,
            trackEnabled: track.enabled,
            detectionTriggered: true
          });
          
          // If call is ending properly (agent finished), clean up gracefully
          if (isCallEndingProperlyRef.current) {
            console.log("✅ [PROCTORED] DETECTION RESULT: Call ending properly - graceful screen share cleanup (NO VIOLATION)");
            cleanupScreenShare();
            return;
          }
          
          // CRITICAL: Handle unexpected screen share termination during proctored session
          setMediaState((currentState) => {
            console.log("🔍 [PROCTORED] VIOLATION ANALYSIS - State check for unexpected termination:", {
              screenSharingActive: currentState.screenSharingActive,
              callActive: callActive,
              connected: connected,
              isStartingScreenShare: isStartingScreenShareRef.current,
              trackStopMethod: trackStopMethod.current,
              analysisTime: new Date().toISOString()
            });
            
            // Show critical violation warning if screen sharing was active during call
            if (currentState.screenSharingActive && callActive && !isStartingScreenShareRef.current) {
              console.log("🚨🚨🚨 [PROCTORED] CRITICAL VIOLATION DETECTED: Screen share stopped during active proctored session");
              
              // Log the violation with context
              const violationContext = {
                violationType: "SCREEN_SHARE_TERMINATION",
                timestamp: endTime,
                callActive,
                connected,
                trackStopMethod: trackStopMethod.current,
                trackReadyState: track.readyState,
                streamSource: currentStreamSourceRef.current,
                trackId: track.id,
                trackKind: track.kind
              };
              
              
              // CRITICAL PROCTORING SECURITY: ANY SCREEN SHARE TERMINATION DURING ACTIVE SESSION IS A VIOLATION
              // Do not attempt recovery - stopping screen sharing is always a violation in proctored sessions
              
              // Determine the reason based on context
              let violationReason: "user_stopped" | "track_ended" | "permission_denied" = "track_ended";
              
              // Check if this was user-initiated (browser button or manual stop)
              if (trackStopMethod.current === "browser_button") {
                violationReason = "user_stopped";
              } else if (trackStopMethod.current === "app_button") {
                violationReason = "user_stopped";
              } else {
                // Could be manual stop via Chrome's "Stop sharing" button or permission revocation
                violationReason = "user_stopped"; // Treat unknown stops as manual for proctoring security
              }
              
              // Immediate violation handling without any recovery attempts
              setTimeout(() => {
                handleScreenShareEnd(violationReason);
              }, 0);
            } else {
              console.log("✅ [PROCTORED] DETECTION RESULT: Screen share ended outside active session - safe cleanup (NO VIOLATION):", {
                reason: !currentState.screenSharingActive ? "Screen sharing not active" :
                        !callActive ? "Call not active" :
                        isStartingScreenShareRef.current ? "Starting new screen share" : "Unknown",
                screenSharingActive: currentState.screenSharingActive,
                callActive,
                isStartingScreenShare: isStartingScreenShareRef.current
              });
              setTimeout(() => cleanupScreenShare(), 0);
            }
            
            return currentState; // Don't modify state in this setter
          });
        };

        trackEndHandlersRef.current.set(track, endHandler);
        track.addEventListener("ended", endHandler, { once: true });
        
        console.log(`✅ [PROCTORED] Violation detector successfully attached to ${track.kind} track ${index + 1}:`, {
          trackId: track.id,
          listenerAttached: true,
          monitoringActive: true,
          timestamp: new Date().toISOString()
        });
      });
        },
        { timeoutMs: NEGOTIATION_TIMEOUT_DEFAULT_MS }
      );

      console.log("🔍🔍🔍 [PROCTORED] PROCTORING SESSION NOW FULLY ACTIVE:", {
        timestamp: new Date().toISOString(),
        screenSharingActive: true,
        callActive,
        connected,
        violationDetectionEnabled: true,
        monitoringScope: "Screen share termination detection",
        trackMonitoringCount: tracks.length,
        status: "MONITORING_ACTIVE",
        detectorsAttached: tracks.length
      });

      // Send proctoring session start event to backend
      if (room.state === "connected") {
        const encoder = new TextEncoder();
        const proctoringData = encoder.encode(
          JSON.stringify({
            type: "proctored_screen_share_started",
            scope: validation.detectedScope,
            confidence: validation.confidence,
            validated: validation.isValid,
            streamSource,
            timestamp: new Date().toISOString(),
            trackCount: tracks.length,
            hasAudio: audioTracks.length > 0,
            resolution: {
              width: videoTracks[0].getSettings().width,
              height: videoTracks[0].getSettings().height,
            }
          })
        );
        room.localParticipant.publishData(proctoringData, { reliable: true });
      }

      // User feedback for successful proctoring setup
      toast({
        title: "Proctored Screen Sharing Active",
        description: `Your entire screen is now being monitored for proctoring compliance (${validation.detectedScope}, confidence: ${Math.round(validation.confidence * 100)}%)`,
      });

      const functionEndTime = Date.now();

    } catch (error) {
      console.error("🎬 [PROCTORED] Critical error establishing screen share session:", error);

      const errorType = handleCloudError(error as Error, 'screen share setup');

      // Log critical screen share establishment failure to Sentry
      const categorizedError = logCategorizedError(error, {
        component: 'useMediaControls',
        operation: 'startScreenShare',
        callId,
        organizationId,
        metadata: {
          errorType,
          roomState: room?.state,
          retryCount: screenShareRetryCountRef.current,
          isScreenSharingActive: mediaState.screenSharingActive,
          callActive,
          connected
        }
      });

      // --- LiveKit Cloud retry logic for negotiation timeouts or connection errors
      if (
        error instanceof Error &&
        isCloudNegotiationError(error) &&
        room?.state !== "disconnected" &&
        !mediaState.screenSharingActive &&
        screenShareRetryCountRef.current < 3 // More retries for Cloud
      ) {
        screenShareRetryCountRef.current += 1;
        const retryDelay = Math.min(2000 * Math.pow(1.5, screenShareRetryCountRef.current - 1), 8000); // Exponential backoff for Cloud

        // Log retry attempt to Sentry
        logRetryAttempt('startScreenShare', screenShareRetryCountRef.current, 3, error, {
          component: 'useMediaControls',
          callId,
          organizationId,
          metadata: {
            errorType,
            retryDelay,
            roomState: room?.state
          }
        });

        console.warn("🔄 [PROCTORED] Retrying screen share publish after LiveKit Cloud error (attempt", screenShareRetryCountRef.current, ") - delay:", retryDelay + "ms");

        setTimeout(() => {
          // Wait until LiveKit Cloud connection is fully stable before retry
          const waitForStableConnection = () => {
            if (room?.state === "connected" &&
                !mediaState.screenSharingActive &&
                room.engine?.client?.ws?.readyState === WebSocket.OPEN) {
              // Additional small delay for Cloud stability + debouncing
              screenShareDebouncer.current.scheduleNext(() => {
                if (room?.state === "connected" && !mediaState.screenSharingActive) {
                  startScreenShare();
                }
              });
            } else if (room?.state !== "disconnected" && screenShareRetryCountRef.current < 4) {
              setTimeout(waitForStableConnection, 1000);
            }
          };
          waitForStableConnection();
        }, retryDelay);
      }
      
      // Clear the starting flag on any error
      isStartingScreenShareRef.current = false;
      
      // Provide specific error handling for proctored sessions
      let errorTitle = "Screen Sharing Failed";
      let errorDescription = "Unable to start screen sharing for proctored session.";
      
      if (error instanceof Error) {
        if (error.name === "NotAllowedError") {
          errorTitle = "Permission Denied";
          errorDescription = "Screen sharing permission is required for proctored sessions. Please allow screen sharing and try again.";
        } else if (error.name === "NotFoundError") {
          errorTitle = "No Display Found";
          errorDescription = "No display available for sharing. Please ensure you have a monitor connected.";
        } else if (error.name === "NotSupportedError") {
          errorTitle = "Browser Not Supported";
          errorDescription = "Your browser doesn't support screen sharing. Please use Chrome for proctored sessions.";
        }
      }
      
      toast({
        title: errorTitle,
        description: errorDescription,
        variant: "destructive",
      });
    }
  }, [
    room,
    mediaState.screenSharingActive,
    validatedScreenStream,
    detectAndValidateScreenShareScope,
    toast,
    handleScreenShareEnd,
    cleanupScreenShare,
    callActive,
    connected,
    runWithNegotiationGate,
  ]);

  const stopScreenShare = useCallback(() => {
    console.log("🎬 [PROCTORED] User-initiated screen share stop requested");
    
    // Mark this as app-initiated stop to distinguish from violations
    trackStopMethod.current = "app_button";
    
    // Set call ending properly flag to prevent violation warnings for user-initiated stops
    isCallEndingProperlyRef.current = true;
    
    // Mark screen sharing as inactive first to prevent track end handler from treating it as unexpected
    setMediaState((prev) => ({ ...prev, screenSharingActive: false }));
    
    // Handle the screen share end gracefully
    handleScreenShareEnd("user_stopped");
    
    // Reset the call ending flag after a short delay
    setTimeout(() => {
      isCallEndingProperlyRef.current = false;
    }, 1000);
    
    console.log("🎬 [PROCTORED] User-initiated screen share stop completed");
  }, [handleScreenShareEnd]);

  // Initialize to clean state
  useEffect(() => {
    trackStopMethod.current = "unknown";
    isCallEndingProperlyRef.current = false;
    isStartingScreenShareRef.current = false;
    isProcessingScreenShareEndRef.current = false;
  }, []);
  
  // Debug effect to monitor warning state
  useEffect(() => {
    // Monitor screen share warning state changes
  }, [mediaState.showScreenShareEndedWarning, mediaState.screenShareEndReason, mediaState.screenSharingActive]);
  
  // Enhanced monitoring for manual screen share termination detection
  const monitorScreenShareEvents = useCallback((stream: MediaStream) => {
    console.log("🔍 [PROCTORED] Setting up enhanced screen share termination detection");
    
    // Record the timestamp when monitoring starts
    const monitoringStartTime = Date.now();
    
    // Monitor stream inactive event - often triggered by manual stops
    stream.addEventListener('inactive', () => {
      trackStopTimestamp.current = Date.now();
      
      // Stream becoming inactive during active session suggests manual termination
      if (mediaState.screenSharingActive && trackStopMethod.current === "unknown") {
        trackStopMethod.current = "browser_button";
      }
    });
    
    // Monitor each video track for ended events (more reliable for manual detection)
    const videoTracks = stream.getVideoTracks();
    videoTracks.forEach((track, index) => {
      console.log(`🔍 [PROCTORED] Setting up track ${index + 1} termination detection:`, {
        trackId: track.id,
        trackLabel: track.label,
        readyState: track.readyState
      });
      
      // Track termination is now handled purely by event-driven detection (no polling)
      // This eliminates interference with LiveKit Cloud's negotiation processes
    });
    
    // Monitor for permission changes (Chrome specific)
    navigator.permissions?.query({ name: 'camera' as PermissionName }).then(permission => {
      permission.addEventListener('change', () => {
        if (permission.state === 'denied' && mediaState.screenSharingActive) {
          console.log('🚨 [PROCTORED] MANUAL TERMINATION DETECTED: Permission denied while screen sharing');
          trackStopMethod.current = "browser_button";
        }
      });
    }).catch(() => {
      // Permissions API not available or screen capture permission not queryable
      console.log('🔍 [PROCTORED] Permissions API not available for manual termination detection');
    });
    
    // Additional heuristic: Check for rapid stream termination (likely manual)
    setTimeout(() => {
      if (!stream.active && trackStopMethod.current === "unknown") {
        console.log('🚨 [PROCTORED] MANUAL TERMINATION DETECTED: Stream terminated quickly after start - likely manual');
        trackStopMethod.current = "browser_button";
      }
    }, 500); // If stream ends within 500ms of starting monitoring, likely manual
    
    console.log("✅ [PROCTORED] Enhanced screen share termination detection setup complete");
  }, [mediaState.screenSharingActive]);

  const pauseCallForMonitorDetection = useCallback(
    (reason: "monitor_detected") => {
      setMediaState((prev) => ({
        ...prev,
        callPaused: true,
        pauseReason: reason,
      }));

      // Mute local microphone during pause
      if (room && room.state === "connected") {
        room.localParticipant.setMicrophoneEnabled(false);
      }

      // Mute remote audio
      if (remoteAudioRef.current) {
        remoteAudioRef.current.muted = true;
      }

      // Send pause event to room participants
      if (room && room.state === "connected") {
        const encoder = new TextEncoder();
        const pauseData = encoder.encode(
          JSON.stringify({
            type: "call_paused",
            reason: reason,
            timestamp: new Date().toISOString(),
          })
        );
        room.localParticipant.publishData(pauseData, { reliable: true });
      }
    },
    [room]
  );

  const resumeCall = useCallback(() => {
    setMediaState((prev) => ({
      ...prev,
      callPaused: false,
      pauseReason: undefined,
      screenShareInterrupted: false,
    }));

    // Re-enable microphone if it was enabled before pause
    if (room && room.state === "connected" && mediaState.isMuted === false) {
      room.localParticipant.setMicrophoneEnabled(true);
    }

    // Unmute remote audio if speaker was not muted
    if (remoteAudioRef.current && !mediaState.isSpeakerMuted) {
      remoteAudioRef.current.muted = false;
    }

    // Send resume event to room participants
    if (room && room.state === "connected") {
      const encoder = new TextEncoder();
      const resumeData = encoder.encode(
        JSON.stringify({
          type: "call_resumed",
          timestamp: new Date().toISOString(),
        })
      );
      room.localParticipant.publishData(resumeData, { reliable: true });
    }
  }, [room, mediaState.isMuted, mediaState.isSpeakerMuted]);


  const dismissScreenShareEndedWarning = useCallback(() => {
    setMediaState((prev) => ({
      ...prev,
      showScreenShareEndedWarning: false,
      screenShareEndReason: undefined,
    }));
  }, []);

  const dismissCameraDisconnectedWarning = useCallback(() => {
    setMediaState((prev) => ({
      ...prev,
      showCameraDisconnectedWarning: false,
      cameraDisconnectReason: undefined,
      cameraInterrupted: false,
    }));
  }, []);

  const handleReconnectCamera = useCallback(async () => {
    // Dismiss the warning first
    dismissCameraDisconnectedWarning();

    // Re-enable video
    await toggleVideo();
    
    // Resume call after camera reconnect
    resumeCallAfterCameraReconnect();
  }, [dismissCameraDisconnectedWarning, toggleVideo, resumeCallAfterCameraReconnect]);

  const setCallEndingProperly = useCallback((isEnding: boolean) => {
    isCallEndingProperlyRef.current = isEnding;
    if (isEnding) {
      lastEndingSignalTimeRef.current = Date.now();
    }
    console.log(`Call ending properly flag set to: ${isEnding}`);
  }, []);

  // Track recent microphone operations to prevent camera interference
  const recentMicrophoneOperationRef = useRef<number>(0);
  
  // Track if enableLocalMedia is currently running to prevent race conditions
  const enableLocalMediaInProgressRef = useRef<boolean>(false);
  const enableLocalMediaPromiseRef = useRef<Promise<void> | null>(null);

  // Monitor camera track for unexpected disconnections and validate stream health
  useEffect(() => {
    if (!room || !callActive) return;

    // Track the current camera track listener to prevent memory leaks
    // This prevents MaxListenersExceededWarning when handleTrackPublished is called multiple times
    let currentCameraTrackSid: string | null = null;
    let currentCleanup: (() => void) | null = null;

    const checkCameraTrack = () => {
      const localParticipant = room.localParticipant;
      const cameraPublication = Array.from(localParticipant.videoTrackPublications.values())
        .find(pub => pub.source === Track.Source.Camera);

      if (cameraPublication && cameraPublication.track) {
        const track = cameraPublication.track;
        const trackSid = track.sid || track.mediaStreamID || 'unknown';

        // Skip if we already have a listener for this exact track
        if (currentCameraTrackSid === trackSid) {
          return currentCleanup;
        }

        // Clean up previous listener if track changed
        if (currentCleanup) {
          currentCleanup();
          currentCleanup = null;
          currentCameraTrackSid = null;
        }

        // Monitor track ended event
        const handleTrackEnded = () => {
          console.log("Camera track ended unexpectedly");
          // Check if call is ending properly
          if (isCallEndingProperlyRef.current) {
            console.log("Camera track end ignored - call is ending properly");
            return;
          }
          // Don't trigger warning if call is ending properly
          if (mediaState.isVideoEnabled && !mediaState.showCameraDisconnectedWarning) {
            // Additional check for recent ending signals
            const now = Date.now();
            if (lastEndingSignalTimeRef.current && (now - lastEndingSignalTimeRef.current) < 3000) {
              console.log("Camera track end ignored - recent ending signal detected");
              return;
            }
            // Check if this is due to recent microphone operation
            if (now - recentMicrophoneOperationRef.current < 2000) {
              console.log("Camera track end ignored - recent microphone operation detected");
              return;
            }
            handleCameraDisconnect("track_ended");
          }
        };

        track.on("ended", handleTrackEnded);
        currentCameraTrackSid = trackSid;

        currentCleanup = () => {
          track.off("ended", handleTrackEnded);
          currentCameraTrackSid = null;
        };

        return currentCleanup;
      }
    };

    // Check immediately and when tracks change
    checkCameraTrack();

    const handleTrackPublished = () => {
      checkCameraTrack(); // Now properly handles cleanup internally
    };
    
    const handleTrackUnpublished = (pub: LocalTrackPublication) => {
      if (pub.source === Track.Source.Camera && mediaState.isVideoEnabled && !mediaState.showCameraDisconnectedWarning) {
        console.log("Camera track unpublished unexpectedly");
        // Check if call is ending properly
        if (isCallEndingProperlyRef.current) {
          console.log("Camera unpublish ignored - call is ending properly");
          return;
        }
        // Additional check for recent ending signals
        const now = Date.now();
        if (lastEndingSignalTimeRef.current && (now - lastEndingSignalTimeRef.current) < 3000) {
          console.log("Camera unpublish ignored - recent ending signal detected");
          return;
        }
        // Check if this is due to recent microphone operation
        if (now - recentMicrophoneOperationRef.current < 2000) {
          console.log("Camera unpublish ignored - recent microphone operation detected");
          return;
        }
        handleCameraDisconnect("device_disconnected");
      }
    };
    
    // Periodic validation of camera stream health (with safety guards)
    let recoveryAttempts = 0;
    const maxRecoveryAttempts = 2; // Reduced from 3 to be less aggressive
    let healthCheckInterval: NodeJS.Timeout | null = null;
    
    const validateCameraHealth = async () => {
      // Safety check - don't run if call is not active or ending
      if (!callActive || !mediaState.isVideoEnabled || !localVideoRef.current || isCallEndingProperlyRef.current) {
        return;
      }
      
      // Don't run camera health check if there was a recent microphone operation
      const now = Date.now();
      if (now - recentMicrophoneOperationRef.current < 3000) {
        console.log("Camera health check skipped - recent microphone operation");
        return;
      }
      
      try {
        const stream = localVideoRef.current.srcObject as MediaStream;
        let needsRecovery = false;
        
        if (!stream || !stream.active) {
          // Give more time before declaring stream as needing recovery
          // Stream might be temporarily inactive during normal operations
          console.log("Camera health check: Stream appears inactive, waiting before recovery...");
          
          // Wait a bit and recheck
          await new Promise(resolve => setTimeout(resolve, 2000));
          
          // Recheck after waiting
          const recheckStream = localVideoRef.current?.srcObject as MediaStream;
          if (!recheckStream || !recheckStream.active) {
            needsRecovery = true;
            console.warn("Camera health check: Stream still inactive after recheck");
          } else {
            console.log("Camera health check: Stream recovered on its own");
            return; // Stream recovered, no action needed
          }
        } else {
          const videoTracks = stream.getVideoTracks();
          if (videoTracks.length === 0) {
            needsRecovery = true;
            console.warn("Camera health check: No video tracks");
          } else {
            const track = videoTracks[0];
            const settings = track.getSettings();
            
            // Be much more tolerant - only intervene in serious cases
            if (track.readyState === 'ended' || (settings.width && settings.width <= 2 && settings.height && settings.height <= 2)) {
              needsRecovery = true;
              console.warn("Camera health check: Track is ended or definitely placeholder", {
                readyState: track.readyState,
                width: settings.width,
                height: settings.height
              });
            } else {
              // Track seems fine, reset recovery attempts
              if (recoveryAttempts > 0) {
                console.log("Camera health check: Track appears healthy, resetting recovery attempts");
                recoveryAttempts = 0;
              }
            }
          }
        }
        
        if (needsRecovery && recoveryAttempts < maxRecoveryAttempts && !isCallEndingProperlyRef.current) {
          // Additional safety check - don't recover if recent microphone operation
          if (now - recentMicrophoneOperationRef.current < 3000) {
            console.log("Camera recovery skipped - recent microphone operation");
            return;
          }
          
          recoveryAttempts++;
          console.log(`Attempting careful camera recovery (${recoveryAttempts}/${maxRecoveryAttempts})`);
          
          try {
            // More gentle recovery - don't disable first, just try to re-enable
            await room.localParticipant.setCameraEnabled(true, {
              resolution: {
                width: 1280,
                height: 720,
                frameRate: 30,
              },
            });
            
            // Give more time for recovery
            setTimeout(() => {
              const newStream = localVideoRef.current?.srcObject as MediaStream;
              if (newStream?.active && newStream.getVideoTracks().length > 0) {
                const track = newStream.getVideoTracks()[0];
                const settings = track.getSettings();
                if (settings.width && settings.width > 2) {
                  recoveryAttempts = 0;
                  console.log("Camera recovery successful");
                }
              }
            }, 3000); // Longer wait time
          } catch (error) {
            console.error("Camera recovery failed:", error);
          }
        } else if (needsRecovery && recoveryAttempts >= maxRecoveryAttempts) {
          console.error("Camera recovery failed after max attempts - genuine camera issue detected");
          // Only show warning for genuine camera failures
          if (!isCallEndingProperlyRef.current) {
            handleCameraDisconnect("device_disconnected");
          }
        }
      } catch (error) {
        console.error("Camera health check error:", error);
      }
    };
    
    // Only run health check if call is active and camera is enabled
    // Reduced frequency for LiveKit Cloud to prevent interference
    if (callActive && mediaState.isVideoEnabled && !isCallEndingProperlyRef.current && 
        room?.state === 'connected') {
      // Very long interval for LiveKit Cloud to minimize interference with negotiation processes
      healthCheckInterval = setInterval(validateCameraHealth, 600000); // 10 minutes for maximum Cloud stability
    }
    
    room.localParticipant.on("localTrackPublished", handleTrackPublished);
    room.localParticipant.on("localTrackUnpublished", handleTrackUnpublished);
    
    return () => {
      // Use currentCleanup directly instead of cleanup to ensure we clean up the latest listener
      // This prevents memory leaks when handleTrackPublished updated the listener
      currentCleanup?.();
      if (healthCheckInterval) {
        clearInterval(healthCheckInterval);
        healthCheckInterval = null;
      }
      room.localParticipant.off("localTrackPublished", handleTrackPublished);
      room.localParticipant.off("localTrackUnpublished", handleTrackUnpublished);
    };
  }, [room, callActive, mediaState.isVideoEnabled, mediaState.showCameraDisconnectedWarning, handleCameraDisconnect, localVideoRef]);

  // Helper to apply audio output device
  const applyAudioOutputDevice = useCallback(async () => {
    
    if (!remoteAudioRef.current) {
      return;
    }
    
    if (!selectedDevices?.audioOutputDeviceId) {
      return;
    }
    
    if (!('setSinkId' in remoteAudioRef.current)) {
      return;
    }

    
    try {
      // Check current sinkId before changing
      const currentSinkId = (remoteAudioRef.current as any).sinkId;
      
      if (currentSinkId === selectedDevices.audioOutputDeviceId) {
        return;
      }
      
      // Wait for audio element to be ready if needed
      if (remoteAudioRef.current.readyState === 0) {
        await new Promise((resolve) => {
          remoteAudioRef.current!.addEventListener('loadedmetadata', resolve, { once: true });
          // Timeout after 5 seconds
          setTimeout(resolve, 5000);
        });
      }
      
      await (remoteAudioRef.current as any).setSinkId(selectedDevices.audioOutputDeviceId);
      
    } catch (error) {
      console.error('❌ [applyAudioOutputDevice] Failed to apply audio output device:', error);
      console.error('❌ [applyAudioOutputDevice] Error details:', { 
        name: error.name,
        message: error.message,
        selectedDevice: selectedDevices.audioOutputDeviceId
      });
    }
  }, [selectedDevices?.audioOutputDeviceId]);

  // Set the ref to the function for use in handleRemoteTrack
  applyAudioOutputDeviceRef.current = applyAudioOutputDevice;

  // Apply audio output device when it changes or when audio ref changes
  useEffect(() => {
    applyAudioOutputDevice();
  }, [selectedDevices?.audioOutputDeviceId, applyAudioOutputDevice]);

  // Also monitor for when audio element gets a source
  useEffect(() => {
    if (!remoteAudioRef.current) return;
    
    const audioElement = remoteAudioRef.current;
    
    const handleCanPlay = () => {
      applyAudioOutputDevice();
    };
    
    audioElement.addEventListener('canplay', handleCanPlay);
    audioElement.addEventListener('loadedmetadata', handleCanPlay);
    
    return () => {
      audioElement.removeEventListener('canplay', handleCanPlay);
      audioElement.removeEventListener('loadedmetadata', handleCanPlay);
    };
  }, [applyAudioOutputDevice]);

  // Cleanup effect for audio contexts and animation frames
  useEffect(() => {
    return () => {
      // Clean up all audio contexts
      audioContextsRef.current.forEach((context, trackId) => {
        context.close();
      });
      audioContextsRef.current.clear();

      // Cancel all audio monitoring timeouts
      animationFramesRef.current.forEach((timeoutId) => {
        clearTimeout(timeoutId);
      });
      animationFramesRef.current.clear();

      // Clean up screen share track listeners
      screenShareTracksRef.current.forEach((track) => {
        const handler = trackEndHandlersRef.current.get(track);
        if (handler) {
          track.removeEventListener("ended", handler);
        }
        track.stop();
      });
      screenShareTracksRef.current = [];
      trackEndHandlersRef.current.clear();
      
      // Clean up screen share debouncer
      screenShareDebouncer.current.cancel();
    };
  }, []);

  return {
    // State
    mediaState,

    // Refs
    localVideoRef,
    remoteVideoRef,
    remoteAudioRef,
    screenShareRef,
    secondaryCameraRef,

    // Actions
    enableLocalMedia,
    toggleMicrophone,
    toggleVideo,
    toggleSpeaker,
    startScreenShare,
    stopScreenShare,
    pauseCallForMonitorDetection,
    resumeCall,
    pauseCallForTabChangePenalty,
    resumeCallAfterTabChangePenalty,
    pauseCallForBlurViolation,
    resumeCallAfterBlurViolation,
    pauseCallForCameraDisconnect,
    resumeCallAfterCameraReconnect,
    pauseCallForFaceOutOfView,
    resumeCallAfterFaceInView,
    dismissScreenShareEndedWarning,
    dismissCameraDisconnectedWarning,
    handleReconnectCamera,
    setCallEndingProperly,

    // Event handlers
    handleRemoteTrack,
    handleRemoteParticipant,
  };
};
